import { z } from 'zod';

// Imported from the file rather than the `media` barrel: both domains are
// re-exported from the package root, and going through the barrel would make
// them circular at module-init time.
import { MediaKindSchema } from '../../media/media-kind/media-kind';
import {
  SettingControlSchema,
  SettingSelectOptionSchema,
} from '../setting-control/setting-control';
import { SettingGroupIconSchema } from '../setting-group-icon/setting-group-icon';
import { SettingManagedBySchema } from '../setting-managed-by/setting-managed-by';
import { SettingScopeTypeSchema } from '../setting-scope/setting-scope';
import { DurationUnitSchema } from '../setting-unit/setting-unit';

/**
 * A **serialized** setting definition — everything the admin needs to render a
 * setting row without knowing the key exists (W8).
 *
 * The definitions themselves live in the api (`shared/settings/registry/`),
 * because they carry runtime Zod types and server policy defaults. This is
 * their wire projection: `valueSchema` is the definition's Zod type rendered as
 * JSON Schema, so the client can validate and shape a control before the round
 * trip — the server still re-validates against the real schema (never trust the
 * projection).
 */
export const SettingDefinitionMetadataSchema = z
  .object({
    key: z.string().min(1),
    /** Sidebar entry. */
    group: z.string().min(1),
    /** Tab within the group. */
    subgroup: z.string().min(1),
    control: SettingControlSchema,
    /** i18n keys, never literal copy (U8). */
    labelKey: z.string().min(1),
    descriptionKey: z.string().min(1),
    /** Position within the subgroup. */
    order: z.number().int(),
    /** Confirm before writing (`useConfirm`) — e.g. `billing.planMode`. */
    dangerous: z.boolean(),
    /** The value is stored per-locale in `setting_value_translations` (N5). */
    translatable: z.boolean(),
    scopesAllowed: z.array(SettingScopeTypeSchema).min(1),
    /** JSON Schema of the value — `z.toJSONSchema(definition.type)`. */
    valueSchema: z.record(z.string(), z.unknown()),
    /** The coded default, always defined — the admin shows it as "default: …". */
    defaultValue: z.unknown(),
    /** Choices for the option-shaped controls; `null` for every other control. */
    options: z.array(SettingSelectOptionSchema).nullable(),
    /**
     * The unit the value is **stored** in — required for `duration`, `null` for
     * every other control.
     *
     * It travels as metadata rather than being inferred from the key's suffix
     * (`…TtlSeconds`), because inference would turn a rename into a production
     * bug: the same integer read as seconds instead of days is a token that
     * expires 86 400 times too soon.
     */
    unit: DurationUnitSchema.nullable(),
    /**
     * Which media kind a `media`-controlled value may point at — required for
     * `media`, `null` for every other control.
     *
     * Metadata for the same reason `unit` is: the picker has to know which
     * upload path and which quota apply *before* it offers a file, and deriving
     * that from the key (`brand.logoMediaId` → "brand something") would make a
     * rename a silent change of upload target.
     */
    mediaKind: MediaKindSchema.nullable(),
    /**
     * The module that owns this value's **editor**, or `null` when the settings
     * screen owns it (P10.4).
     *
     * Required-and-nullable for the reason `unit` and `mediaKind` are: `null`
     * says "Settings owns this", while an *absent* field says the server is
     * older than the client — and the admin would then draw a JSON textarea
     * over a domain it has been told to hand over, which is the exact defect
     * this pointer exists to close (U0).
     *
     * A presentation pointer, never an authorization boundary: the api still
     * accepts a write at a marked key, because the owning module's own editor
     * goes through that same single write path (U9).
     */
    managedBy: SettingManagedBySchema.nullable(),
  })
  .strict();

export type SettingDefinitionMetadata = z.infer<typeof SettingDefinitionMetadataSchema>;

/**
 * A subgroup section: its ordered settings.
 *
 * **The array IS the order** — there is deliberately no `order` field here or on
 * the group. A group's and a subgroup's position is *declared* server-side
 * (`setting-groups.ts`) and every undeclared one shares one rank, broken on the
 * name; publishing that rank would put duplicate numbers on the wire while the
 * tie-break stayed behind, so a client that re-sorted by it would reintroduce
 * exactly the ordered-by-chance defect P10.3e closed. A setting keeps its
 * `order`, because that one is a declared fact rather than a derived rank.
 */
export const SettingSubgroupMetadataSchema = z
  .object({
    subgroup: z.string().min(1),
    labelKey: z.string().min(1),
    settings: z.array(SettingDefinitionMetadataSchema).min(1),
  })
  .strict();

export type SettingSubgroupMetadata = z.infer<typeof SettingSubgroupMetadataSchema>;

/** A sidebar group: its ordered subgroup sections. */
export const SettingGroupMetadataSchema = z
  .object({
    group: z.string().min(1),
    labelKey: z.string().min(1),
    /**
     * Which glyph the sidebar draws for this group — a semantic name from a
     * closed union, chosen in the api's group declaration (W8: a group is
     * created there, so its identity travels from there).
     */
    icon: SettingGroupIconSchema,
    subgroups: z.array(SettingSubgroupMetadataSchema).min(1),
  })
  .strict();

export type SettingGroupMetadata = z.infer<typeof SettingGroupMetadataSchema>;

/**
 * The whole registry, grouped and ordered — one request, and the settings
 * screen exists. A new definition file appears here with zero admin UI code.
 */
export const SettingsRegistryMetadataSchema = z
  .object({
    groups: z.array(SettingGroupMetadataSchema),
  })
  .strict();

export type SettingsRegistryMetadata = z.infer<typeof SettingsRegistryMetadataSchema>;
